import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin } from "state";
import FlexBetween from "components/FlexBetween";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(""); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); 
    setError("");

    // Client-side validation
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include", // ✅ VERY IMPORTANT for cookies

      });

      const data = await response.json();
      console.log(
      "this is data>>:",
        data
      );
      

      if (!response.ok) {
        throw new Error(
          data.message || 
          data.error?.message || 
          "Login failed. Please try again."
        );
      }

      if (!data.token || !data.user) {
        throw new Error("Invalid response from server");
      }
      
      // Update Redux state
      dispatch(setLogin({
        user: data.user,
        token: data.token
      }));

      // Force refresh to ensure all auth checks run
      navigate("/dashboard"); // This performs client-side navigation
    } catch (err) {
      // Handle different error types
      let errorMessage = "An error occurred during login";
      
      if (err.message.includes("Failed to fetch")) {
        errorMessage = "Unable to connect to server. Please check your connection.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        width="100%"
        maxWidth="500px"
        p="2rem"
        borderRadius="0.55rem"
        sx={{
          backgroundColor: theme.palette.background.alt,
          boxShadow: theme.shadows[4],
        }}
      >
        <FlexBetween flexDirection="column" gap="1.5rem">
          <Typography variant="h2" color={theme.palette.secondary[500]}>
            Welcome Back
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ width: "100%" }}
              onClose={() => setError("")} // Allow dismissing errors
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                error={Boolean(error)}
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              />

              <TextField
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                error={Boolean(error)}
                InputProps={{
                  style: {
                    color: theme.palette.secondary[200],
                    backgroundColor: theme.palette.background.default,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                sx={{
                  p: "1rem",
                  backgroundColor: theme.palette.secondary[500],
                  color: theme.palette.background.alt,
                  "&:hover": {
                    backgroundColor: theme.palette.secondary[600],
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Login"
                )}
              </Button>
            </Box>
          </form>

          <Typography>
            Don't have an account?{" "}
            <span
              style={{
                color: theme.palette.secondary[500],
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => navigate("/register")}
            >
              Register here
            </span>
          </Typography>
        </FlexBetween>
      </Box>
    </Box>
  );
};

export default Login;